import type { Lead } from "../../lib/types";
import { comparableText } from "../../ui/text";
import { civilDate, daysBetween, type Notice, type NoticeCategory } from "./feed";

/**
 * Caixa de notificações — o recorte que a central exibe.
 *
 * O feed já vem derivado e ordenado de `feed.ts`. Aqui só se decide o que
 * entra na seleção corrente e como os avisos se agrupam por dia. Tudo é
 * função pura sobre o feed, para a contagem de cada aba e de cada caixa sair
 * do mesmo cálculo da lista exibida (AGENTS §10).
 */

export const NOTICE_VIEWS = [
  { id: "todas", label: "Todas" },
  { id: "nao-lidas", label: "Não lidas" },
  { id: "pendentes", label: "Pendentes" },
  { id: "resolvidas", label: "Resolvidas" },
] as const;

export type NoticeViewId = (typeof NOTICE_VIEWS)[number]["id"];

export const DEFAULT_NOTICE_VIEW: NoticeViewId = "todas";

/** O rótulo acompanha o prefixo "Período:" da barra, como no protótipo. */
export const NOTICE_PERIODS = [
  { id: "todos", label: "Todos" },
  { id: "hoje", label: "Hoje" },
  { id: "semana", label: "Últimos 7 dias" },
] as const;

export type NoticePeriodId = (typeof NOTICE_PERIODS)[number]["id"];

export function isNoticeViewId(value: string | null): value is NoticeViewId {
  return NOTICE_VIEWS.some((view) => view.id === value);
}

export function isNoticePeriodId(value: string | null): value is NoticePeriodId {
  return NOTICE_PERIODS.some((period) => period.id === value);
}

/**
 * Um aviso resolvido sai da operação e só aparece na aba própria.
 *
 * Resolver não conserta a causa, mas encerra o acompanhamento: mantê-lo nas
 * outras abas faria o contador de pendências nunca baixar (§18).
 */
export function matchesNoticeView(notice: Notice, view: NoticeViewId): boolean {
  switch (view) {
    case "todas":
      return !notice.resolvedAt;
    case "nao-lidas":
      return !notice.resolvedAt && !notice.readAt;
    case "pendentes":
      return !notice.resolvedAt && notice.actionable;
    case "resolvidas":
      return Boolean(notice.resolvedAt);
    default:
      return true;
  }
}

export function matchesNoticePeriod(
  notice: Notice,
  period: NoticePeriodId,
  today: string,
  timeZone?: string,
): boolean {
  if (period === "todos") return true;
  const day = civilDate(notice.occurredAt, timeZone);
  const distance = daysBetween(day, today);
  if (period === "hoje") return distance === 0;
  // "Esta semana" vai sete dias para trás e sete para a frente: uma fatura a
  // vencer tem data futura e é exatamente o que a semana de trabalho precisa
  // mostrar. Por isso o rótulo não diz "últimos 7 dias".
  return distance <= 7 && distance >= -7;
}

export interface NoticeFilterInput {
  notices: readonly Notice[];
  view: NoticeViewId;
  /** `null` quando nenhuma categoria está selecionada. */
  category: NoticeCategory | null;
  funnelId: string | null;
  period: NoticePeriodId;
  query: string;
  today: string;
  timeZone?: string;
  /** Usado só para a busca alcançar nome e empresa do lead. */
  leads?: readonly Lead[];
}

export function filterNotices(input: NoticeFilterInput): Notice[] {
  const needle = comparableText(input.query);
  const leadById = new Map((input.leads ?? []).map((lead) => [lead.id, lead]));
  return input.notices.filter((notice) => {
    if (!matchesNoticeView(notice, input.view)) return false;
    if (input.category && notice.category !== input.category) return false;
    if (input.funnelId && notice.funnelId !== input.funnelId) return false;
    if (!matchesNoticePeriod(notice, input.period, input.today, input.timeZone)) return false;
    if (!needle) return true;
    const lead = notice.leadId ? leadById.get(notice.leadId) : undefined;
    const haystack = comparableText(
      [notice.title, notice.detail, lead?.name ?? "", lead?.company ?? ""].join(" "),
    );
    return haystack.includes(needle);
  });
}

export interface NoticeDayGroup {
  /** Dia civil do grupo; vazio nas pendências e sem data utilizável. */
  day: string;
  label: string;
  notices: Notice[];
}

/** Rótulo do dia, relativo ao hoje do usuário. */
export function noticeDayLabel(day: string, today: string): string {
  const distance = daysBetween(today, day);
  if (distance === 0) return "Hoje";
  if (distance === -1) return "Ontem";
  if (distance > 0) return "Próximas datas";
  const [year, month, date] = day.split("-");
  return `${date}/${month}/${year}`;
}

/** Seção das pendências, separada da cronologia (rótulo do protótipo). */
export const PENDING_GROUP_LABEL = "Pendências ativas";

/**
 * Agrupa por dia, com as pendências em uma seção própria.
 *
 * Dentro de cada dia vale a ordem do feed, que já põe o mais grave primeiro.
 * Entre os dias vale a cronologia decrescente: uma caixa datada em que "Hoje"
 * aparece depois de anteontem se lê como defeito, não como prioridade.
 *
 * O que exige ação não é notícia do dia: uma tarefa vencida há dois dias
 * continua pendente hoje. Por isso ela sai da linha do tempo e vai para
 * "Pendências ativas", no fim — é o recorte que o protótipo mostra e o único
 * que responde "o que falta fazer" sem obrigar a varrer os dias.
 */
export function groupNoticesByDay(
  notices: readonly Notice[],
  today: string,
  timeZone?: string,
): NoticeDayGroup[] {
  const groups: NoticeDayGroup[] = [];
  const byLabel = new Map<string, NoticeDayGroup>();
  for (const notice of notices) {
    const day = civilDate(notice.occurredAt, timeZone);
    const pending = notice.actionable && !notice.resolvedAt;
    const label = pending ? PENDING_GROUP_LABEL : day ? noticeDayLabel(day, today) : "Sem data registrada";
    let group = byLabel.get(label);
    if (!group) {
      group = { day: pending ? "" : day, label, notices: [] };
      byLabel.set(label, group);
      groups.push(group);
    }
    group.notices.push(notice);
  }
  // Sem data — e as pendências, que não têm cronologia — vão para o fim.
  return groups.sort((a, b) => {
    if (!a.day) return 1;
    if (!b.day) return -1;
    return b.day.localeCompare(a.day);
  });
}

export interface NoticeSummary {
  total: number;
  unread: number;
  pending: number;
}

/** Resumo do cabeçalho, sobre o feed inteiro e não sobre a seleção. */
export function summarizeNotices(notices: readonly Notice[]): NoticeSummary {
  let unread = 0;
  let pending = 0;
  let total = 0;
  for (const notice of notices) {
    if (notice.resolvedAt) continue;
    total += 1;
    if (!notice.readAt) unread += 1;
    if (notice.actionable) pending += 1;
  }
  return { total, unread, pending };
}
